import { Component, OnInit } from '@angular/core';
import { AssetsService } from '../assets.service';
import { Router } from '@angular/router';
import { IpfsService } from '../ipfs.service';
import { MatSnackBar } from '@angular/material/snack-bar';

export interface Asset {
  id: string;
  name: string;
  desc: string;
  balance: string;
}

@Component({
  selector: 'ssi-assets',
  templateUrl: './assets.component.html',
  styleUrls: ['./assets.component.scss'],
})
export class AssetsComponent implements OnInit {
  key!: string;
  assets: Asset[] = [];
  isLoading: boolean = true;

  private readonly ZERO_TOKEN_ID = '0x0000000000000000000000000000000000000000000000000000000000000000';

  constructor(
    private assetsService: AssetsService,
    private ipfsService: IpfsService,
    private router: Router,
    private snackBar: MatSnackBar
  ) {}

  async ngOnInit(): Promise<void> {
    this.key = this.assetsService.getPrivateKey();
    if (this.key) {
      this.assetsService.setPublicKey();
      await this.loadAssets();
    } else {
      this.router.navigate(['/onboarding']);
    }
  }

  async loadAssets(): Promise<void> {
    this.isLoading = true;
    try {
      console.log('Loading assets for:', this.getPublicKey());
      this.assets = await this.getAllAssets();
      console.log('Assets loaded:', this.assets);
    } catch (error) {
      console.error('Error loading assets:', error);
      this.snackBar.open('Error loading assets', 'Close', { duration: 3000 });
    } finally {
      this.isLoading = false;
    }
  }

  async refreshAssets(): Promise<void> {
    await this.loadAssets();
    this.snackBar.open('Assets refreshed', 'Close', { duration: 2000 });
  }

  public getPublicKey(): string {
    if (this.key) return this.assetsService.getPublicKeyFromPrivateKey(this.key);
    return '';
  }

  async getAllAssets(): Promise<Asset[]> {
    const publicKey = this.getPublicKey();
    const assetList: Asset[] = [];

    try {
      // Get zero token balance first (this should always work)
      let zeroBalance = '0';
      try {
        const balance = await this.assetsService.call('pm', 'balanceOf', [publicKey, this.ZERO_TOKEN_ID]);
        zeroBalance = balance?.toString() || '0';
      } catch (e) {
        console.warn('Error fetching zero token balance:', e);
      }

      // Add zero token at the beginning
      assetList.push({
        id: this.ZERO_TOKEN_ID,
        name: 'Unrestricted Funds',
        desc: 'No spending restrictions',
        balance: zeroBalance
      });

      // Get asset list and balances
      let assetIdList: string[] = [];
      let assetBalanceList: any[] = [];

      try {
        assetIdList = await this.assetsService.call('pm', 'assetsOf(address)', [publicKey]);
        // Ensure it's an array
        if (!Array.isArray(assetIdList)) {
          assetIdList = [];
        }
      } catch (e) {
        console.warn('Error fetching asset IDs:', e);
        assetIdList = [];
      }

      try {
        assetBalanceList = await this.assetsService.call('pm', 'assetBalanceOf', [publicKey]);
        // Ensure it's an array
        if (!Array.isArray(assetBalanceList)) {
          assetBalanceList = [];
        }
      } catch (e) {
        console.warn('Error fetching asset balances:', e);
        assetBalanceList = [];
      }

      // Process assets with matching balances
      for (let i = 0; i < assetIdList.length; i++) {
        const assetId = assetIdList[i];
        const balance = assetBalanceList[i];

        // Skip zero token (already added) and zero balances
        if (assetId === this.ZERO_TOKEN_ID) continue;
        if (!balance || balance.toString() === '0') continue;

        const assetName = await this.getAssetName(assetId);
        assetList.push({
          id: assetId,
          name: assetName,
          desc: '',
          balance: balance.toString()
        });
      }

    } catch (error) {
      console.error('Error in getAllAssets:', error);
    }

    return assetList;
  }

  async getAssetName(id: string): Promise<string> {
    try {
      const policyRaw = await this.ipfsService.getMeta(id);
      if (policyRaw) {
        const policy = typeof policyRaw === 'string' ? JSON.parse(policyRaw) : policyRaw;
        if (policy?.name) {
          return policy.name;
        }
      }
    } catch (error) {
      console.warn('Error getting asset name for', id, error);
    }
    return `Asset ${id.substring(0, 8)}...`;
  }

  routeTransfer(id: string): void {
    this.router.navigate(['/assets/transfer/' + id]);
  }

  copyAddress(): void {
    const address = this.getPublicKey();
    navigator.clipboard.writeText(address).then(() => {
      this.snackBar.open('Address copied to clipboard', 'Close', { duration: 2000 });
    });
  }

  async getZeroTokens(): Promise<void> {
    try {
      const tx = await this.assetsService.getZeroTokens(
        'pm',
        'buyProgrammableMoney(address,uint256)',
        [this.getPublicKey(), 100]
      );

      this.snackBar.open('Requesting tokens...', 'Close', { duration: 2000 });

      const result = await tx.wait();
      if (result.status) {
        this.snackBar.open('Tokens received!', 'Close', { duration: 3000 });
        await this.loadAssets();
      } else {
        this.snackBar.open('Transaction failed', 'Close', { duration: 3000 });
      }
    } catch (error) {
      console.error('Error requesting tokens:', error);
      this.snackBar.open('Error requesting tokens', 'Close', { duration: 3000 });
    }
  }
}
